import { LogoutOptions } from '@auth0/auth0-react';
import { StatusCodes } from 'http-status-codes';
import { action, computed, observable, runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { backendUrl } from '../config/app';
import { storageService } from '../services/storage.service';
import { getUnleashClient } from '../services/unleash-feature-flags.service';
import { MyIDEXXUserType } from '../types/brm-types';
import { PracticeOffersResponse, PracticeResponse } from '../types/practices-types';
import { SpeciesBreedsResponse } from '../types/species-breeds-types';
import { ResourcePermission, SapResourcePermission, UserApi } from '../types/users-types';
import { getPathWithQuery } from '../utils/url-utils';
import AbstractStore from './AbstractStore';
import { OFFERS_PATH, PRACTICES_PATH, SPECIES_BREEDS_PATH, USERS_PATH } from './constants';
import RootStore from './RootStore';

const USER_EMAIL = 'USER_EMAIL';

export default class ApiStore extends AbstractStore {
  public logout?: (options?: LogoutOptions) => Promise<void>;

  private getAccessTokenSilently?: (...args: any) => Promise<string>;

  private readonly backEndUrl: string;

  private _userGuid = uuidv4();

  public constructor(rootStore: RootStore) {
    super(rootStore);
    this.userEmail = storageService.getItem(USER_EMAIL, true);
    this.backEndUrl = backendUrl();
  }

  @observable accessor user: UserApi | undefined;

  @observable accessor userEmail: string;

  @observable private accessor _isAuthenticated = false;

  @computed
  public get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  @computed
  public get userName(): string {
    return `${this.user?.firstName} ${this.user?.lastName}`;
  }

  @computed
  public get userPermissions(): ResourcePermission[] {
    return ([] as ResourcePermission[]).concat(
      ...(this.user?.globalResourcePermissions || []),
      ...(this.sapId
        ? this.user?.sapResourcePermissions?.find(srp => srp.sapId === this.sapId)
            ?.resourcePermissions || []
        : []),
    );
  }

  @computed
  public get userGlobalPermissions(): ResourcePermission[] {
    return this.user?.globalResourcePermissions || [];
  }

  @computed
  public get userSapPermissions(): SapResourcePermission[] {
    return this.user?.sapResourcePermissions || [];
  }

  get userGuid(): any {
    return this._userGuid;
  }

  set userGuid(userGuid: string) {
    this._userGuid = userGuid;
    const unleashClient = getUnleashClient();
    if (unleashClient) {
      void unleashClient.updateContext({ userId: this.userEmail, sessionId: userGuid });
    }
  }

  @action
  public authenticate(
    getAccessTokenSilently: (...args: any) => Promise<string>,
    logout: (options?: LogoutOptions) => Promise<void>,
    user: MyIDEXXUserType | undefined,
  ): void {
    this.getAccessTokenSilently = getAccessTokenSilently;
    this.logout = logout;
    this._isAuthenticated = true;
    this.userEmail = user?.email || '';
    // setting userId for WalkMe
    (window as any).userId = user?.sub;
    storageService.replace(USER_EMAIL, this.userEmail, true);
  }

  // *** users

  @action
  public async fetchCurrentUserDetails(): Promise<void> {
    this.userGuid = uuidv4();
    const users = await this.callBackendApi<UserApi[]>(USERS_PATH);
    if (users) {
      runInAction(() => (this.user = users[0]));
    }
  }

  // *** species/breeds
  @action
  public async fetchSpeciesAndBreeds(sapId: string): Promise<SpeciesBreedsResponse> {
    return this.callBackendApi<SpeciesBreedsResponse>(
      getPathWithQuery(SPECIES_BREEDS_PATH, { sapId }),
    );
  }

  // *** practices

  @action
  public async fetchPractices(sapId: string): Promise<PracticeResponse[]> {
    return this.callBackendApi<PracticeResponse[]>(getPathWithQuery(PRACTICES_PATH, { sapId }));
  }

  // *** offers

  @action
  public async fetchPracticesOffers(sapId: string): Promise<PracticeOffersResponse[]> {
    return this.callBackendApi<PracticeOffersResponse[]>(getPathWithQuery(OFFERS_PATH, { sapId }));
  }

  @action
  public async callBackendApi<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { headers, ...others } = options;
    if (!this.getAccessTokenSilently) {
      throw new Error(`Auth0 getAccessTokenSilently() method is not set`);
    }
    const accessToken = await this.getAccessTokenSilently();
    // const accessToken = '1234567890';
    const res = await fetch(`${this.backEndUrl}${endpoint}`, {
      ...others,
      headers: { ...headers, Authorization: `Bearer ${accessToken}`, 'x-user-guid': this.userGuid },
    });
    const data = await res.text();
    if (res.status !== StatusCodes.OK.valueOf()) {
      throw new Error(`Http error ${res.status}: ${data}`);
    }
    return JSON.parse(data);
  }

  @action.bound
  public async backendPost<T = any>(endpoint: string, body: any): Promise<T> {
    return this.callBackendApi<T>(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  @action
  public signOut(): void {
    this.user = undefined;
    this._isAuthenticated = false;
    this.getAccessTokenSilently = undefined;
    if (this.logout) {
      void this.logout({ logoutParams: { returnTo: location.origin } });
    }
  }
}
